import React from 'react';
import { MembershipTier, TIER_INFO, TIER_PRICING, TIER_BENEFITS } from '../types/membership';

interface TierBadgeProps {
  tier: MembershipTier;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function TierBadge({ tier, showLabel = true, size = 'md' }: TierBadgeProps) {
  const info = TIER_INFO[tier];
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  
  return (
    <span 
      className={`
        inline-flex items-center font-bold rounded-full
        ${info.bgColor} ${info.color}
        ${sizeClasses[size]}
      `}
      title={info.description}
    >
      {tier === 'ultra' && (
        <svg 
          className={`w-4 h-4 mr-1 ${size === 'sm' ? 'w-3 h-3 mr-0.5' : ''}`} 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
      {showLabel && <span>{info.name}</span>}
    </span>
  );
}

interface TierCardProps {
  tier: MembershipTier;
  isCurrentTier?: boolean;
  onSelect?: () => void;
}

export function TierCard({ tier, isCurrentTier = false, onSelect }: TierCardProps) {
  const info = TIER_INFO[tier];
  const price = tier !== 'guest' ? TIER_PRICING[tier] : null;
  
  const benefits = TIER_BENEFITS[tier];
  
  return (
    <div
      className={`
        relative rounded-2xl p-6 border-2 transition-all
        ${isCurrentTier
          ? `${info.borderColor} bg-slate-50 dark:bg-slate-800 shadow-lg`
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
        }
        ${onSelect ? 'cursor-pointer hover:shadow-md' : ''}
      `}
      onClick={onSelect}
    >
      {isCurrentTier && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className={`${info.bgColor} ${info.color} px-3 py-1 rounded-full text-xs font-bold`}>
            Current Plan
          </span>
        </div>
      )}
      
      <div className="text-center mb-4">
        <TierBadge tier={tier} size="lg" />
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
          {info.description}
        </p>
      </div>
      
      {price !== null && (
        <div className="text-center mb-4">
          <div className="flex items-baseline justify-center">
            <span className="text-4xl font-bold text-slate-900 dark:text-white">${price}</span>
            <span className="text-slate-500 dark:text-slate-400 ml-1">/month</span>
          </div>
        </div>
      )}
      
      <ul className="space-y-2">
        {benefits.map((feature, index) => (
          <li key={index} className="flex items-center text-sm text-slate-600 dark:text-slate-300">
            <svg 
              className={`w-4 h-4 mr-2 ${info.color}`} 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
}
